import { useState } from 'react';
import { Avatar, Button, Icon, MaskedValue, Pill, SlotKey, type Patient as DsPatient } from '@etnas/design-system';
import { patientsApi, type Patient } from '@/api';
import { fmtDate, fmtShortDate } from '@/lib/format';
import { initialsOf } from '@/lib/patient';

const ID_STATE: Record<string, [string, string, string]> = {
  verified: ['check', 'success', 'ID verified'],
  unverified: ['info', 'urgent', 'ID not verified'],
  temporary: ['urgent', 'urgent', 'Temporary identity'],
  confidential: ['shield', 'sensitive', 'Confidential patient'],
};

/**
 * Persistent patient banner (zone 5). Same markup and classes as the design-system PatientBanner, extended for
 * ETNAS data: NKA vs NKDA vs not assessed stay distinct (ALG-001) with the real verification date, the
 * restricted-records flag, and expandable details (care team, coverage, proxy, masked SSN with audited reveal).
 * Never scrolls, never truncates the legal name.
 */
export function PatientBannerX({ patient: p, slot = 1 }: { patient: Patient; slot?: 1 | 2 | 3 | 4 }) {
  const [expanded, setExpanded] = useState(false);
  const b = p.banner;
  const id = ID_STATE[p.identityStatus] ?? ID_STATE.verified;
  const sex = p.sex === 'female' ? 'Female' : p.sex === 'male' ? 'Male' : p.sex === 'other' ? 'Other' : 'Unknown';
  return (
    <header className={`ex-banner ex-slot-${slot}${p.identityStatus === 'temporary' ? ' is-temporary' : ''}${p.deceased ? ' is-deceased' : ''}`} aria-label={`Patient: ${p.name.display}`}>
      <div className="ex-banner-id">
        <Avatar initials={initialsOf(p.name.display)} slot={slot} size={44} />
        <div className="ex-banner-names">
          <div className="ex-banner-line1">
            <SlotKey slot={slot} />
            <h2 className="ex-banner-name">{p.name.display}</h2>
            {p.name.preferred && p.name.preferred !== p.name.given[0] ? <span className="ex-banner-pref">“{p.name.preferred}”</span> : null}
          </div>
          <div className="ex-banner-line2">
            <span>
              <span className="ex-lbl">DOB</span>
              <span className="ex-mono">{p.dob}</span> <b>{p.age}</b>
            </span>
            <span>
              {sex}
              {p.pronouns ? <span className="ex-fg-muted"> · {p.pronouns}</span> : null}
            </span>
            <span>
              <span className="ex-lbl">MRN</span>
              <span className="ex-mono">{p.mrn}</span>
            </span>
            <span className={`ex-fg-${id[1]} ex-idstate`}>
              <Icon name={id[0]} size={12} knockout="var(--surface)" />
              {id[2]}
            </span>
            {p.deceased ? (
              <span className="ex-fg-muted ex-idstate">
                <Icon name="minus" size={12} />
                Deceased {p.deceasedAt ? fmtDate(p.deceasedAt) : ''}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="ex-banner-cell">
        <div className="ex-lbl">Encounter</div>
        {b.encounter ? (
          <>
            <div className="ex-banner-val">
              <b>{b.encounter.type}</b> · {b.encounter.place}
            </div>
            <div className="ex-banner-sub">
              {b.encounter.attending} · <span className="ex-mono">{b.encounter.since}</span>
            </div>
          </>
        ) : (
          <div className="ex-banner-sub">No active encounter</div>
        )}
      </div>
      <div className="ex-banner-cell ex-banner-allergy">
        <div className="ex-lbl">Allergies</div>
        <BannerAllergies p={p} />
      </div>
      <div className="ex-banner-cell ex-banner-flags">
        <div className="ex-lbl">Alerts</div>
        <div className="ex-flags">
          {b.restricted ? (
            <Pill tone="sensitive" icon="shield" size="sm">
              Restricted records
            </Pill>
          ) : null}
          {b.flags.filter((f) => !(b.restricted && /restricted/i.test(f.label))).map((f, i) => (
            <Pill key={i} tone={f.tone ?? 'neutral'} icon={f.icon} size="sm">
              {f.label}
            </Pill>
          ))}
          {!b.flags.length && !b.restricted ? <span className="ex-small ex-fg-subtle">None</span> : null}
        </div>
      </div>
      <div className="ex-banner-tools">
        <Button variant="quiet" size="sm" icon={expanded ? 'chevron-down' : 'more'} aria-label={expanded ? 'Hide patient details' : 'Show patient details'} aria-expanded={expanded} onClick={() => setExpanded((e) => !e)} />
      </div>
      {expanded ? <BannerDetails p={p} /> : null}
    </header>
  );
}

function BannerAllergies({ p }: { p: Patient }) {
  const b = p.banner;
  const verified = b.allergyVerifiedAt ? <span className="ex-fg-subtle"> · recorded {fmtShortDate(b.allergyVerifiedAt)}</span> : null;
  if (b.allergyStatus === 'nka')
    return (
      <span className="ex-allergy-none">
        <Icon name="check" size={12} />
        No known allergies {verified}
      </span>
    );
  if (b.allergyStatus === 'nkda')
    return (
      <span className="ex-allergy-none" title="No known drug allergies. Food and environmental allergies were not asserted.">
        <Icon name="check" size={12} />
        No known drug allergies (NKDA) {verified}
      </span>
    );
  if (b.allergyStatus === 'unknown')
    return (
      <Pill tone="urgent" icon="urgent">
        Allergy status unknown — not the same as none
      </Pill>
    );
  if (b.allergyStatus === 'not-assessed')
    return (
      <Pill tone="urgent" icon="urgent">
        Allergies not assessed
      </Pill>
    );
  const max = 2;
  return (
    <span className="ex-allergies">
      {b.allergies.slice(0, max).map((a, i) => (
        <span key={i} className={`ex-allergy is-${a.severity}`} title={`${a.substance} — ${a.reaction} (${a.severity})`}>
          {a.severity === 'severe' ? <Icon name="critical" size={12} knockout="var(--critical-soft)" /> : null}
          <b>{a.substance}</b>
          <span>{a.reaction}</span>
          <span className="ex-visually-hidden">, {a.severity}</span>
        </span>
      ))}
      {b.allergies.length > max ? <span className="ex-more" title={b.allergies.slice(max).map((a) => a.substance).join(', ')}>+{b.allergies.length - max}</span> : null}
    </span>
  );
}

function BannerDetails({ p }: { p: Patient }) {
  const [ssn, setSsn] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const reveal = async () => {
    if (ssn) return setSsn(null);
    setRevealing(true);
    try {
      const ids = await patientsApi.identifiers(p.id);
      setSsn(ids.items.find((i) => i.type === 'SSN')?.value ?? 'Not on file');
    } finally {
      setRevealing(false);
    }
  };
  const rows: [string, React.ReactNode][] = [
    ['Care team', p.banner.careTeam ?? 'None recorded'],
    ['Coverage', p.banner.coverage ?? '—'],
    ['Language', `${p.language}${p.interpreterNeeded ? ' · interpreter needed' : ''}`],
    ['Code status', p.banner.codeStatus ?? 'Not documented'],
    ['Proxy', p.banner.proxy ?? 'None'],
    [
      'SSN',
      p.ssnLast4 ? (
        <span aria-busy={revealing || undefined}>
          <MaskedValue value={ssn ?? `000-00-${p.ssnLast4}`} revealed={!!ssn} onReveal={reveal} />
        </span>
      ) : (
        'Not on file'
      ),
    ],
  ];
  return (
    <div className="ex-banner-more">
      {rows.map(([l, v]) => (
        <div key={l}>
          <div className="ex-lbl">{l}</div>
          <div>{v}</div>
        </div>
      ))}
    </div>
  );
}

/** Adapter for DS components that take the DS Patient shape. */
export type { DsPatient };
